<template>
    <div class="container-fluid px-4">

        <BaseTable :rows="workgroups" :columns="columns" :loading="loading" searchable :selectable="false"
            :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
            @searchChanged="onSearchChanged">

            <template #buttons>
                <material-button v-if="!readOnly" @click="showAddWorkgroup = true" variant="secondary" color="info" class="px-4 mx-2"
                    :disabled="isSaving">
                    <i class="material-icons text-lg me-1">add</i>
                    Add New Instance/Date
                </material-button>
                <material-button v-if="!readOnly" @click="showDeleteAllMessage = true" variant="secondary" color="danger"
                    class="px-4 mx-2">
                    <i class="material-icons text-lg me-1">delete</i>
                    Delete All Future Instances
                </material-button>
            </template>

            <template #col-instanceSummary="{ row }">
                <button class="btn btn-sm btn-outline-success my-0" @click="openInstanceSummary(row)">
                    <i class="material-icons text-sm align-middle">summarize</i>
                </button>
            </template>

            <template #actions="{ row }">
                <button v-if="!readOnly" class="btn btn-sm my-0 py-0" @click="editInstances(row.activityinstanceid)">
                    <i class="material-icons text-lg text-orange">edit</i>
                </button>
                <button v-if="readOnly" class="btn btn-sm my-0 py-0" @click="editInstances(row.activityinstanceid)">
                    <i class="material-icons text-lg" style="color: #6c757d;">visibility</i>
                </button>
                <button v-if="!readOnly" class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
                    <i class="material-icons text-lg text-danger">delete</i>
                </button>
                <button class="btn btn-sm my-0 py-0" @click="openLogsModal(row)">
                    <i class="material-icons text-lg text-info">chat</i>
                </button>
            </template>
        </BaseTable>

        <ContentModal v-model="showAddWorkgroup" title="Add New Instance/Date" :showClose="false">
            <div class="card-body">
                <form @submit.prevent="addInstance">
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <MaterialDatePicker id="instance-start-date" label="Start Date" v-model="instancesData.instancestartdate"
                                :readOnly="true" />
                        </div>

                        <div class="col-md-6">
                            <MaterialInput id="instance-start-time" type="time" label="Start Time" v-model="instancesData.instancestarttime"
                                />
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-md-6">
                            <MaterialSpinnerInput id="instance-duration" label="Duration (minutes)"
                                v-model="instancesData.instanceduration" :min="1" :read-only="true" />
                        </div>

                        <div class="col-md-6">
                            <MaterialSelect label="Auto-Schedule" v-model="instancesData.instanceautoschedule"
                                :options="yesNoOptions" />
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-md-6">
                            <MaterialSelect label="Status" v-model="instancesData.instancestatus"
                                :options="statusOptions" />
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-md-12">
                            <MaterialTextarea label="Notes" v-model="instancesData.instancenote"
                                placeholder="Enter notes" rows="3" />
                        </div>
                    </div>
                </form>
            </div>
            <template #footer>

                <div class="d-flex justify-content-end gap-2">
                    <MaterialButton @click="addInstance" variant="primary" :class="['px-4', hasChanges && !isSaving ? 'pulse-glow' : '']" :disabled="isSaving">
                        <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                            aria-hidden="true"></span>
                        <i v-else class="material-icons text-lg me-1">save</i>
                        {{ isSaving ? 'Saving...' : 'Save' }}
                    </MaterialButton>
                    <MaterialButton variant="secondary" class="px-4" color="danger" type="button" @click="resetForm">
                        <i class="material-icons text-lg me-1">block</i>
                        Cancel
                    </MaterialButton>
                </div>

            </template>
        </ContentModal>

        <ContentModal v-model="showDeleteAllMessage" title="Delete All Future Instances" :showClose="false">
            <p>
                Are you sure you want to delete all future instances for this Activity?
            </p>

            <div class="form-check mt-3">
                <input class="form-check-input" type="checkbox" id="resetLastInstanceDate"
                    v-model="resetLastInstanceDateChecked" />
                <label class="form-check-label" for="resetLastInstanceDate">
                    Reset the Last Instance date (to regenerate all instances)
                </label>
            </div>

            <template #footer>
                <div class="d-flex justify-content-end gap-2">
                    <material-button @click="deleteAllInstances" variant="secondary" class="px-4" :disabled="isSaving">
                        <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <i v-else class="material-icons text-lg me-1">check</i>
                        {{ isSaving ? 'Deleting...' : 'Yes' }}
                    </material-button>
                    <material-button @click="showDeleteAllMessage = false" variant="secondary" class="px-4"
                        :disabled="isSaving">
                        <i class="material-icons text-lg me-1">close</i>
                        No
                    </material-button>
                </div>
            </template>
        </ContentModal>

        <ContentModal v-model="showLogs" title="Logs">
            <BaseTable :columns="logColumns" :rows="logs" :searchable="false" :pagination="false" class="mt-3" />
        </ContentModal>

        <DeleteModal v-model:show="showDeleteModal" :itemName="selectedInstance?.workgroupName"
            @confirm="deleteInstances" />

        <SummaryModal v-model="showInstanceSummaryModal" :title="instanceSummaryTitle" :summaryList="instanceSummaryData" />
    </div>
</template>

<script setup>
import { ref, onMounted, computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { useRoute, useRouter } from 'vue-router'
import BaseTable from '@/components/Table/Table.vue'
import DeleteModal from '@/components/Modal/ConfirmationModal.vue'
import MaterialInput from '@/components/common/MaterialInput.vue'
import MaterialSelect from '@/components/common/MaterialSelect.vue'
import MaterialButton from '../../../components/common/MaterialButton.vue'
import ContentModal from '../../../components/Modal/ContentModal.vue'
import MaterialTextarea from '../../../components/common/MaterialTextarea.vue'
import MaterialDatePicker from '@/components/common/MaterialDatePicker.vue'
import MaterialSpinnerInput from '@/components/common/MaterialSpinnerInput.vue'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import SummaryModal from '@/components/Modal/SummaryModal.vue'

defineProps({
    readOnly: { type: Boolean, default: false }
})

const toast = useToast()
const store = useStore()
const route = useRoute()
const router = useRouter()

const searchTerm = ref('')
const showDeleteModal = ref(false)
const selectedInstance = ref(null)
const loading = ref(true)
const showAddWorkgroup = ref(false);
const showDeleteAllMessage = ref(false);
const resetLastInstanceDateChecked = ref(true);
const showLogs = ref(false);
const logs = computed(() => store.getters.auditLogs || []);
const logsLoading = ref(false);

const locationId = route.params.locationid
const activityId = route.params.activityid

const workgroups = computed(() => store.getters.instances || [])
const pagination = computed(() => store.getters.instancesPagination)

const columns = [
    { key: 'activityinstanceid', label: 'ID' },
    { key: 'startdate', label: 'Start Date' },
    { key: 'enddate', label: 'End Date' },
    { key: 'status', label: 'Status' },
    { key: 'completedautoschedule', label: 'Completed Auto-Schedule' },
    { key: 'instanceSummary', label: 'Summary' },
    { key: 'lastautoscheduledate', label: 'Last Auto-Schedule Date' },
    { key: 'last_admin_alert_dt', label: 'Last Admin Alert Date' },
    { key: 'note', label: 'Note' },
]

const logColumns = [
    { key: "messagedt", label: "Date/Time" },
    { key: "message", label: "Message" },
];

const showInstanceSummaryModal = ref(false)
const instanceSummaryData = ref([])
const instanceSummaryTitle = ref('')

const openInstanceSummary = async (row) => {
    instanceSummaryTitle.value = `Instance Summary: ${row.startdate}`
    try {
        instanceSummaryData.value = await store.dispatch('fetchActivityInstanceSummary', row.activityinstanceid)
        showInstanceSummaryModal.value = true
    } catch (e) {
        console.error('Error loading instance summary:', e)
    }
}

const fetchInstances = async (page = 1, pageSize = 10) => {
    loading.value = true
    try {
        await store.dispatch('fetchInstances', {
            activityId,
            startdate: new Date().toISOString().split('T')[0],
            rangeMonths: 12,
            search: searchTerm.value || '',
            page,
            pageSize,
        })
    } catch (error) {
        console.error('Error fetching workgroups:', error)
        toast.error('Failed to load workgroups', { position: 'top-right' })
    } finally {
        loading.value = false
    }
}

const onChangePage = (page) => {
    if (!page) return
    fetchInstances(page)
}

const onUpdatePageSize = (size) => {
    fetchInstances(1, size)
}

const onSearchChanged = (term) => {
    searchTerm.value = term
    fetchInstances(1)
}

const openDeleteModal = (workgroup) => {
    selectedInstance.value = workgroup
    showDeleteModal.value = true
}

const openLogsModal = async (workgroup) => {
    logsLoading.value = true;

    try {
        const response = await store.dispatch("fetchAuditLogs", workgroup.activityinstanceid);

        console.log("response", response);
        
        if (response?.length === 0) {
            toast.info("No logs found for this instance.", { position: "top-right" });
        }
        else {
            showLogs.value = true;
        }
    } catch (error) {
        console.error("Failed to fetch logs:", error);
    } finally {
        logsLoading.value = false;
    }
}

const deleteAllInstances = async () => {
    isSaving.value = true;
    try {
        var response = await store.dispatch("deleteFutureInstances", {
            locationId,
            activityId,
        });
        toast.success(response?.message || 'Future instances deleted successfully', { position: "top-right" });

        // If checkbox is checked, also reset the last instance date
        if (resetLastInstanceDateChecked.value) {
            try {
                await store.dispatch("resetLastInstanceDate", { activityId, locationId });
                toast.success('Last Instance date has been reset', { position: "top-right" });
            } catch (resetError) {
                console.error('Error resetting last instance date:', resetError);
                toast.warning('Instances deleted but failed to reset last instance date', { position: "top-right" });
            }
        }

        fetchInstances();
    } catch (error) {
        console.log(error);
        toast.error('Failed to delete future instances', { position: "top-right" });
    } finally {
        showDeleteAllMessage.value = false;
        isSaving.value = false;
    }
}

const { pushCrumb } = useBreadcrumb()

const editInstances = (id) => {
    // Find the row to get a meaningful label
    const row = workgroups.value.find(r => r.activityinstanceid === id)
    pushCrumb({
        label: row ? `Instance ${row.startdate || '#' + id}` : `Instance #${id}`,
        icon: 'date_range',
        route: { name: 'editActivityInstance', params: { locationid: locationId, activityinstanceid: id } },
    })
    router.push({
        name: 'editActivityInstance',
        params: { locationId, activityinstanceid: id },
    })
}

const deleteInstances = async () => {
    if (!selectedInstance.value) return;
    isSaving.value = true;
    try {
        const response = await store.dispatch("deleteInstance", {
            locationId,
            instanceId: selectedInstance.value.activityinstanceid,
        });

        // Check response for success (returncode === 0 or "0" means success)
        const isSuccess = response && (response.returncode === 0 || response.returncode === "0");
        
        if (isSuccess) {
            const message = response?.message || "Instance deleted successfully";
            toast.success(message, { position: "top-right" });
            // Refresh the instances list
            const currentPage = pagination.value?.currentPage || 1;
            await fetchInstances(currentPage);
        } else {
            const message = response?.message || "Failed to delete instance";
            toast.error(message, { position: "top-right" });
        }
    } catch (error) {
        console.error("Error deleting instance:", error);
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete instance";
        toast.error(errorMessage, { position: "top-right" });
    } finally {
        showDeleteModal.value = false;
        selectedInstance.value = null;
        isSaving.value = false;
    }
}

const isSaving = ref(false)
const baseline = ref(null)

const hasChanges = computed(() => {
    if (!baseline.value) return false
    return JSON.stringify(baseline.value) !== JSON.stringify({ ...instancesData })
})

const yesNoOptions = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
]

const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'In Active', value: 'inactive' },
]

const instancesData = reactive({
    instancestartdate: '',
    instancestarttime: '',
    instanceduration: 60,
    instanceautoschedule: false,
    instancestatus: 'active',
    instancenote: '',
})

const addInstance = async () => {

    if (!instancesData.instancestartdate || !instancesData.instancestarttime) {
        toast.warning('Start Date and Time are required', { position: 'top-right' })
        return
    }

    isSaving.value = true

    try {
        const payload = {
            activityid: activityId,
            ...instancesData,
        }

        await store.dispatch('addInstance', payload)
        toast.success('Instance added successfully!', { position: 'top-right' })
        showAddWorkgroup.value = false
        resetForm()
        fetchInstances()

    } catch (error) {
        toast.error('Error adding instance', { position: 'top-right' })
    } finally {
        isSaving.value = false
    }
}

const resetForm = () => {
    const activityDetails = store.getters.activityDetails
    instancesData.instancestartdate = ''
    instancesData.instancestarttime = ''
    instancesData.instanceduration = 60
    instancesData.instanceautoschedule = activityDetails?.autoschedule ? 'yes' : 'no'
    instancesData.instancestatus = 'active'
    instancesData.instancenote = ''
    showAddWorkgroup.value = false
    baseline.value = { ...instancesData }
}

onMounted(async () => {
    fetchInstances()

    // Pre-populate auto-schedule from activity details
    const activityDetails = store.getters.activityDetails
    if (activityDetails) {
        instancesData.instanceautoschedule = activityDetails.autoschedule ? 'yes' : 'no'
    }

    baseline.value = { ...instancesData }
})
</script>